import axios from "axios";
import { deviceId } from "@/lib/device";
import { PAY_BUSY, payBusy, payRequest, payWatch } from "@/lib/payQueue";

const ENV_BACKEND = process.env.REACT_APP_BACKEND_URL;
const sameHost = (u) => {
  try {
    return new URL(u).host === window.location.host;
  } catch {
    return false;
  }
};
// Custom domains also serve /api: stay same-origin so session cookies remain first-party.
export const BACKEND = window.location.protocol === "https:" && !sameHost(ENV_BACKEND) ? window.location.origin : ENV_BACKEND;

export const api = axios.create({ baseURL: `${BACKEND}/api`, withCredentials: true });

const PASS_KEY = "fmo_queue_pass";
export const QUEUE_EVENT = "fmo:queue-required";
export const setQueuePass = (token) => localStorage.setItem(PASS_KEY, token);

let inflightJoin = null;
// One join per visitor even under StrictMode double effects / concurrent callers.
export function joinQueue() {
  if (!inflightJoin) {
    inflightJoin = api.post("/queue/join").then((r) => {
      if (r.data?.token) setQueuePass(r.data.token);
      return r;
    }).finally(() => {
      inflightJoin = null;
    });
  }
  return inflightJoin;
}

api.interceptors.request.use((config) => {
  const pass = localStorage.getItem(PASS_KEY);
  if (pass) config.headers["X-Queue-Pass"] = pass;
  config.headers["X-Device-Id"] = deviceId();
  payRequest(config);
  config.__payStop = payWatch(api, config);
  return config;
});

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const IDEMPOTENT = ["get", "head", "options", "put", "delete"];
const AUTH_PROBES = /^\/(auth\/(me|login|session)|dev\/auth\/(me|login|logout))/;

// Expired session on a protected screen: back to the right login page (never on the session probes themselves).
function sessionExpired(url = "") {
  const p = window.location.pathname;
  if (AUTH_PROBES.test(url)) return;
  if (p.startsWith("/admin") && p !== "/admin/login" && url.startsWith("/admin")) {
    window.location.assign(`/admin/login?next=${encodeURIComponent(p)}`);
  } else if (p.startsWith("/espace-developpeur") && !/connexion|mot-de-passe|reinitialiser/.test(p) && url.startsWith("/dev")) {
    window.location.assign("/espace-developpeur/connexion");
  }
}

api.interceptors.response.use(
  (r) => {
    r.config?.__payStop?.();
    return r;
  },
  async (err) => {
    const status = err?.response?.status;
    const cfg = err?.config;
    cfg?.__payStop?.();
    if (status === 503 && cfg?.__payTicket && PAY_BUSY.test(err.response.data?.detail || "")) return payBusy(api, err);
    // Pass lost (backend restart / other replica): re-take it silently and replay once; the queue screen only if really waiting.
    if (status === 429 && err.response.data?.code === "QUEUE_REQUIRED") {
      if (cfg && !cfg.__queueRetried) {
        cfg.__queueRetried = true;
        try {
          const { data } = await joinQueue();
          if (["active", "admin"].includes(data.status)) return api(cfg);
        } catch {
          /* fall through to the queue screen */
        }
      }
      window.dispatchEvent(new Event(QUEUE_EVENT));
    }
    // Network error / 502-504: retry with backoff (idempotent calls; POST only on bare gateway errors, never an app 503 with detail).
    const transient = !axios.isCancel(err) && (!err?.response || [502, 503, 504].includes(status));
    const method = (cfg?.method || "get").toLowerCase();
    const allowed = IDEMPOTENT.includes(method) || ([502, 503].includes(status) && !err.response?.data?.detail);
    if (cfg && cfg.retry !== false && transient && allowed && (cfg.__tries || 0) < 3) {
      cfg.__tries = (cfg.__tries || 0) + 1;
      await sleep(400 * 2 ** (cfg.__tries - 1) + Math.random() * 200);
      return api(cfg);
    }
    if (status === 401) sessionExpired(cfg?.url);
    return Promise.reject(err);
  },
);

export const absUrl = (path) => (path ? `${BACKEND}${path}` : null);

export function formatBytes(bytes, units = ["o", "Ko", "Mo", "Go", "To"], locale = "fr-FR") {
  if (!bytes && bytes !== 0) return "—";
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toLocaleString(locale, { maximumFractionDigits: i === 0 ? 0 : 1 })} ${units[i]}`;
}

export function formatDate(iso, locale = "fr-FR") {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" });
}
